import { useCallback, useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import { AiGlobalStats, Top3User, UserQuota } from "../types/ai-dashboard"
import { AlertFilter, getAiUsageRequest, setUserAiQuotaRequest, SetUserQuotaPayload } from "../_requests"

const today = new Date().toISOString().split('T')[0]; // "2026-04-28"
const firstDayOfYear = `${new Date().getFullYear()}-01-01`; // "2026-01-01"

const DEFAULT_STATS: AiGlobalStats = {
     totalTokens: 0,
     inputNormal: 0,
     cachedTokens: 0,
     outputTokens: 0,
     totalCost: 0,
     documentsCount: 0,
}

export const useAiDashboardData = () => {
     const [stats, setStats] = useState<AiGlobalStats>(DEFAULT_STATS)
     const [top3, setTop3] = useState<Top3User[]>([])
     const [users, setUsers] = useState<UserQuota[]>([])
     const [total, setTotal] = useState<number>(0)
     const [perPage, setPerPage] = useState<number>(10)
     const [isLoading, setIsLoading] = useState<boolean>(true)

     const [search, setSearch] = useState<string>("")
     const [page, setPage] = useState<number>(1)
     const [filter, setFilter] = useState<AlertFilter>("all")
     const [startDate, setStartDate] = useState<string>(firstDayOfYear)
     const [endDate, setEndDate] = useState<string>(today)

     const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

     // Single centralized fetch function
     const loadData = useCallback(() => {
          setIsLoading(true)
          getAiUsageRequest(search, page, filter, startDate, endDate)
               .then(r => {
                    setStats(r.data.stats)
                    setTop3(r.data.top3)
                    setUsers(r.data.users)
                    setTotal(r.data.total)
                    setPerPage(r.data.perPage)
               })
               .catch(e => {
                    toast.error(e?.response?.data?.detail ?? "Erreur lors du chargement des données IA.", { autoClose: false })
               })
               .finally(() => setIsLoading(false))
     }, [search, page, filter, startDate, endDate])

     // Trigger loadData when parameters change
     useEffect(() => {
          if (debounceRef.current) clearTimeout(debounceRef.current)

          // Debounce search, but immediate load for other filters
          if (search) {
               debounceRef.current = setTimeout(loadData, 400)
          } else {
               loadData()
          }

          return () => {
               if (debounceRef.current) clearTimeout(debounceRef.current)
          }
     }, [loadData, search])

     const handleSearchChange = (value: string) => {
          setSearch(value)
          setPage(1)
     }
     const handlePageChange = (newPage: number) => {
          setPage(newPage)
     }
     const handleFilterChange = (newFilter: AlertFilter) => {
          setFilter(newFilter)
          setPage(1)
     }
     const handleDateChange = (start: string, end: string) => {
          setStartDate(start)
          setEndDate(end)
          setPage(1)
     }

     const updateUserQuota = useCallback((userId: number, payload: SetUserQuotaPayload): Promise<void> => {
          return setUserAiQuotaRequest(userId, payload).then(r => {
               const { dailyLimit, monthlyLimit } = r.data
               const updater = (u: UserQuota) => (u.id === userId ? { ...u, dailyLimit, monthlyLimit } : u)
               setUsers(prev => prev.map(updater))
               // setTop3(prev => prev.map(updater))
          })
     }, [])

     return {
          stats,
          top3,
          users,
          total,
          perPage,
          isLoading,
          search,
          page,
          filter,
          startDate,
          endDate,
          onSearchChange: handleSearchChange,
          onPageChange: handlePageChange,
          onFilterChange: handleFilterChange,
          onDateChange: handleDateChange,
          updateUserQuota,
     }
}
